import {
	IonButton,
	IonButtons,
	IonCol,
	IonContent,
	IonFooter,
	IonGrid,
	IonHeader,
	IonIcon,
	IonImg,
	IonItem,
	IonLoading,
	IonModal,
	IonPage,
	IonRow,
	IonText,
	IonTitle,
	IonToast,
	IonToolbar,
} from "@ionic/react";
import { every, filter, get, isEmpty, map as _map, toInteger } from "lodash";
import { useEffect, useState } from "react";
import { arrowBackOutline, thumbsUpOutline } from "ionicons/icons";
import {
	getMaterialStock,
	getPartDetails,
	MaterialStock,
	MetadataObject,
	PlantStock,
	queryMasterdata,
	rateSearch,
} from "../../../api";
import { UserPhoto } from "../../../hooks";
import { catchError, EMPTY, forkJoin, map, switchMap } from "rxjs";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper.min.css";
import "@ionic/react/css/ionic-swiper.css";
import usePartSearch from "../../../context/search.context";
import { useHistory } from "react-router";
import useAuth from "../../../context/auth.context";
import { useSettingsContext } from "../../../context/settings.context";
import { useTranslation } from "react-i18next";
import { DEFAULT_TOAST_DURATION, ION_TEXT_TABLET_STYLE } from '../../../constants';
import { Swiper as SwiperType } from 'swiper/types';

interface SearchResultDetailPageProps {
	barcode: string;
	searchId: string | null;
	searchIndex: string | null;
}

export function SearchResultDetailPage({
	barcode,
	searchId,
	searchIndex,
}: SearchResultDetailPageProps) {
	const [loading, setLoading] = useState<boolean>(false);
	const [photos, setPhotos] = useState<UserPhoto[]>([]);
	const [masterData, setMasterData] = useState<MetadataObject>();
	const [materialStock, setMaterialStock] = useState<MaterialStock>();
	const [materialStockError, setMaterialStockError] =
		useState<boolean>(false);
	const [emptyStockFound, setEmptyStockFound] = useState<boolean>(false);
	const [isRated, setIsRated] = useState<boolean>(false);
	const { acquireToken } = useAuth();
	const { plant, theme } = useSettingsContext();
	// const prioritizedPlant = plant.trim().split("-")[0].trim();
	const [toastMessage, setToastMessage] = useState<string>("");
	const [imageZoomed, setImageZoomed] = useState<boolean>(false);
	const [imageSrc, setImageSrc] = useState<string>("");

	const history = useHistory();
	const { t } = useTranslation();

	const { newSearch } = usePartSearch();

	useEffect(() => {
		setLoading(true);
		acquireToken()
			.pipe(
				switchMap((accessToken) => {
					return forkJoin({
						partDetails: getPartDetails(barcode, accessToken).pipe(
							catchError(() => {
								setMaterialStockError(true);
								setLoading(false);
								return EMPTY;
							})
						),
						masterData: queryMasterdata(barcode, accessToken).pipe(
							catchError(() => {
								setMaterialStockError(true);
								setLoading(false);
								return EMPTY;
							})
						),
						materialStock: getMaterialStock(
							barcode,
							accessToken
						).pipe(
							catchError(() => {
								setMaterialStockError(true);
								setLoading(false);
								return EMPTY;
							}),
							map((response) => {
								const filteredStockResults: PlantStock[] =
									filter(
										response.to_MatlStkInAcctMod.results,
										(it) =>
											toInteger(
												it.MatlWrhsStkQtyInMatlBaseUnit
											) !== 0
									);
								if (
									filteredStockResults.length !==
									response.to_MatlStkInAcctMod.results.length
								) {
									setEmptyStockFound(true);
								}
								return {
									...response,
									to_MatlStkInAcctMod: {
										...response.to_MatlStkInAcctMod,
										results: filteredStockResults,
									},
								};
							})
						),
					});
				})
			)
			.subscribe((response) => {
				const { masterData, materialStock, partDetails } = response;
				setPhotos(partDetails.photos);
				setMasterData(masterData);
				setMaterialStock(materialStock);
				setLoading(false);
			});

		return () => {
			setLoading(false);
		};
	}, []);

	function onNewSearch() {
		newSearch();
		history.push("/search");
	}

	function onAcceptSearch() {
		if (searchId && barcode && searchIndex) {
			setLoading(true);
			setMaterialStockError(true);
			acquireToken()
				.pipe(
					switchMap((accessToken) => {
						return rateSearch(
							barcode,
							searchId,
							searchIndex,
							accessToken
						).pipe(
							catchError((error) => {
								console.error(error);
								setIsRated(false);
								setToastMessage(t("searchResultDetail.rateError"));
								return EMPTY;
							})
						);
					})
				)
				.subscribe(() => {
					setLoading(false);
					setIsRated(true);
				});
		}
	}

	function onBack() {
		history.push("/search-results");
	}

	function isEmptyMaterialStock(materialStock: MaterialStock | undefined) {
		if (!materialStock) {
			return true;
		}
		const results: PlantStock[] = get(
			materialStock,
			"to_MatlStkInAcctMod.results",
			[]
		);

		return (
			!materialStockError &&
			(results.length === 0 ||
				every(
					results,
					(item) =>
						item.Plant === "" &&
						item.Batch === "" &&
						item.StorageLocation === ""
				))
		);
	}

	function masterDataTable(stock: MaterialStock) {
		return (
			<IonGrid fixed={true}>
				<IonRow>
					<IonCol size="2">
						{t("searchResultDetail.plantColumn")}
					</IonCol>
					<IonCol size="2">
						{t("searchResultDetail.storageLocationColumn")}
					</IonCol>
					<IonCol size="3">
						{t("searchResultDetail.batchColumn")}
					</IonCol>
					<IonCol size="5">
						{t("searchResultDetail.baseUnitColumn")}
					</IonCol>
				</IonRow>
				{isEmpty(get(stock, "to_MatlStkInAcctMod.results", [])) && (
					<IonRow>
						<IonCol>
							<IonText color={theme === "dark" ? "warning" : "danger"}>
								{t("searchResultDetail.noStockReturned")}
							</IonText>
						</IonCol>
					</IonRow>
				)}
				{_map(
					get(stock, "to_MatlStkInAcctMod.results", [])
						.sort((itemA: PlantStock, itemB: PlantStock) => {
							return sortByPlant(itemA, itemB);
						})
						.map((item: PlantStock, index) => {
							if (
								item.Plant === "" &&
								item.Batch === "" &&
								item.StorageLocation === ""
							) {
								return null;
							}

							return (
								<IonRow key={`warehouse-row-${index}`}>
									<IonCol size="2">{item.Plant}</IonCol>
									<IonCol size="2">{item.StorageLocation}</IonCol>
									<IonCol size="3">{item.Batch}</IonCol>
									<IonCol size="5">{item.MatlWrhsStkQtyInMatlBaseUnit}</IonCol>
								</IonRow>
							);
						})
				)}
			</IonGrid>
		);
	}

	function sortByPlant(plantA: PlantStock, plantB: PlantStock) {
		// const isPrioritizedA = plantA?.Plant === prioritizedPlant || false;
		// const isPrioritizedB = plantB?.Plant === prioritizedPlant || false;

		// if (isPrioritizedA && !isPrioritizedB) {
		// 	return -1;
		// } else if (!isPrioritizedA && isPrioritizedB) {
		// 	return 1;
		// }

		return 0;
	}

	function handleDoubleTap(_swiper: SwiperType, event: any) {
		const { target } = event;
		const { src } = target;
		setImageSrc(src);
		setImageZoomed(true);
	}

	return (
		<IonPage>
			<IonLoading isOpen={loading} message={t("searchResultDetail.loading")} />
			<IonHeader>
				<IonToolbar>
					<IonButtons slot="start">
						{/* <IonBackButton defaultHref='/search' /> */}
						<IonButton onClick={onBack} size="large">
							<IonIcon icon={arrowBackOutline} slot="icon-only" />
						</IonButton>
					</IonButtons>
					<IonTitle>{barcode}</IonTitle>
				</IonToolbar>
			</IonHeader>
			{/* @ts-ignore */}
			<IonContent>
				<Swiper
					onDoubleTap={handleDoubleTap}
					lazy={true}
					preloadImages={false}
					pagination
					spaceBetween={50}
				>
					{_map(photos, (photo, index) => (
						<SwiperSlide
							key={`photo-slide-${index}`}
							style={{
								marginBottom: "10px",
								maxWidth: "200px",
							}}
						>
							<IonImg src={photo.blobPath} />
						</SwiperSlide>
					))}
				</Swiper>
				<IonItem>
					{loading ? (
						<></>
					) : isEmpty(masterData?.Description) ? (
						<IonText
							color={theme === "dark" ? "warning" : "danger"}
							style={ION_TEXT_TABLET_STYLE}
						>
							{t("searchResultDetail.noStockDataFound")}
						</IonText>
					) : (
						<IonText className="item-text-wrap">
							{masterData?.Description}
						</IonText>
					)}
				</IonItem>
				{loading ? (
					<></>
				) : !isEmptyMaterialStock(materialStock) ? (
					masterDataTable(materialStock!)
				) : (
					<IonItem>
						{materialStockError ? (
							<IonText color="danger" style={ION_TEXT_TABLET_STYLE}>
								{t("searchResultDetail.noConnectionToS4")}
							</IonText>
						) : emptyStockFound ? (
							<IonText
								color={theme === "dark" ? "warning" : "danger"}
								style={ION_TEXT_TABLET_STYLE}
							>
								{t("searchResultDetail.noStockReturned")}
							</IonText>
						) : (
							<IonText
								color={theme === "dark" ? "warning" : "danger"}
								style={ION_TEXT_TABLET_STYLE}
							>
								{t("searchResultDetail.materialNotExtended")}
							</IonText>
						)}
					</IonItem>
				)}
				<IonModal isOpen={imageZoomed}>
					<IonContent>
						<IonImg src={imageSrc} />
					</IonContent>
					<IonButton onClick={() => {
						setImageZoomed(false);
						setImageSrc("");
					}}>
						Close
					</IonButton>
				</IonModal>
			</IonContent>
			<IonFooter>
				<div
					style={{
						display: "flex",
					}}
				>
					<IonButton
						color="primary"
						disabled={!isRated}
						onClick={onNewSearch}
						style={{
							flex: 1,
						}}
					>
						{t("searchResultDetail.newSearch")}
					</IonButton>
					<IonButton
						color="success"
						disabled={isRated}
						onClick={onAcceptSearch}
						style={{
							flex: 1,
						}}
					>
						<IonIcon icon={thumbsUpOutline} slot="end" />
					</IonButton>
				</div>
			</IonFooter>
			<IonToast
				isOpen={!isEmpty(toastMessage)}
				onDidDismiss={() => setToastMessage("")}
				message={toastMessage}
				duration={DEFAULT_TOAST_DURATION}
			/>
		</IonPage>
	);
}
